import React from 'react';
import {
  ImageField,
  ImageMultiSelectField,
  InputField,
  PolymorphicField,
  SelectField,
  VideoField,
  CalendarField,
  TimeField,
  SwitchField,
  TitleField,
  VisualSelectField,
  NumberField,
  RichTextField
} from './fields';
import type { BaseFieldProps, SingleFieldProps } from '../../types/form-renderer';
import { getHandlerForTemplate } from '../../handlers';
import { FORBIDDEN_CHARS } from '../../clients/shared/forbidden-chars';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { VisualSearchField, VisualSearchSingleField } from './fields/visual-search-field';
import { GroupedImageSelectorField } from './fields/grouped-image-selector-field';
import type {
  FormField,
  LoadSubImagesFn,
  VisualSelectOption,
  ItemGroup,
  FormDataValue,
  ValueOf,
  RoutedFormData
} from '../../types/templates';
import type { DynamicOptions } from '../../types/client-handler';
import { loadSubImagesRegistry } from '../../clients/_load-sub-images';
import { useBanners } from '../../context/banners';

interface Props extends SingleFieldProps {
  setFieldError?: (key: string, error?: string) => void;
}

const resolveLoadSubImages = (
  field: Extract<FormField, { type: 'grouped-image-selector' }>,
  formData: RoutedFormData
) => {
  const fn = loadSubImagesRegistry[field.loadSubImagesKey];
  if (!fn) return undefined;
  return (item: Parameters<LoadSubImagesFn>[0], group: Parameters<LoadSubImagesFn>[1]) => fn(item, group, formData);
};

export const FormFieldRenderer: React.FC<Props> = ({
  field: rawField,
  formData,
  onFieldChange,
  errors = {},
  creativityType,
  setFieldError,
  customStyles
}) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const clientHandler = getHandlerForTemplate(creativityType);
  const { folders } = useBanners();
  const field = rawField as FormField;

  if (clientHandler.shouldShowField) {
    const shouldShow = clientHandler.shouldShowField(field.key, formData, creativityType);
    if (!shouldShow) return null;
  }

  const fieldOptions = 'options' in field ? field.options : undefined;
  const getDynamicOptions = (): DynamicOptions | undefined => {
    if (clientHandler.getDynamicOptions) {
      const dynamicOpts = clientHandler.getDynamicOptions(
        field.key,
        { ...formData, _creativityType: creativityType },
        (fieldOptions ?? []) as DynamicOptions,
        { folders }
      );
      if (dynamicOpts) return dynamicOpts;
    }
    return fieldOptions as DynamicOptions | undefined;
  };
  const getSelectOptions = (): VisualSelectOption[] => (getDynamicOptions() ?? []) as VisualSelectOption[];
  const getGroupOptions = (): ItemGroup[] => (getDynamicOptions() ?? []) as ItemGroup[];

  const commonStyle = {
    width: '100%',
    marginBottom: spacing.md,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'flex-start' as const,
    fontSize: screens.xs ? fontSize.lg : fontSize.base
  };

  const labelStyle = {
    fontSize: screens.xs ? fontSize.lg : fontSize.base,
    letterSpacing: '0.5px',
    marginBottom: 8
  };

  function buildProps<F extends FormField>(narrowed: F): BaseFieldProps<F> {
    return {
      field: narrowed,
      value: formData[narrowed.key] as ValueOf<F> | undefined,
      onChange: (value: ValueOf<F>) => onFieldChange(narrowed.key, value as FormDataValue),
      commonStyle,
      labelStyle,
      error: errors[narrowed.key]
    };
  }

  const validateForbiddenChars = (value: string, forbidden: string[]): string | null => {
    if (!forbidden || forbidden.length === 0) return null;
    for (const char of forbidden) {
      if ((value || '').includes(char)) {
        return `El nombre contiene caracteres no permitidos: "${char}"`;
      }
    }
    return null;
  };

  switch (field.type) {
    case 'input': {
      const handleBlur = (value: string) => {
        if (field.key === 'title' && setFieldError) {
          const validationError = validateForbiddenChars(value, FORBIDDEN_CHARS);
          setFieldError(field.key, validationError || undefined);
        }
      };

      if (field.key === 'title') {
        return (
          <TitleField
            key={field.key}
            {...buildProps(field)}
            formData={formData}
            onBlur={() => handleBlur(formData[field.key] as string)}
          />
        );
      }

      // Inputs render as antd AutoComplete when they have options — same source as selects.
      const dynamicOpts = getDynamicOptions();
      const inputField =
        dynamicOpts && dynamicOpts.length > 0
          ? { ...field, options: dynamicOpts as { value: string; label?: string }[] }
          : field;

      return (
        <InputField
          key={field.key}
          {...buildProps(inputField)}
          onBlur={() => handleBlur(formData[field.key] as string)}
        />
      );
    }

    case 'select': {
      if (field.key === 'day' && formData.date) return null;
      return <SelectField key={field.key} {...buildProps({ ...field, options: getSelectOptions() })} />;
    }

    case 'video':
      return <VideoField key={field.key} {...buildProps(field)} />;

    case 'image':
      return <ImageField key={field.key} {...buildProps(field)} />;

    case 'calendar':
      return <CalendarField key={field.key} {...buildProps(field)} formData={formData} onFieldChange={onFieldChange} />;

    case 'time':
      return <TimeField key={field.key} {...buildProps(field)} />;

    case 'switch':
      return <SwitchField key={field.key} {...buildProps(field)} customStyles={customStyles} />;

    case 'visual-select':
      return <VisualSelectField key={field.key} {...buildProps({ ...field, options: getSelectOptions() })} />;

    case 'number':
      return <NumberField key={field.key} {...buildProps(field)} />;

    case 'rich-text': {
      let maxLines = field.maxLines || 1;

      if (clientHandler.getMaxLinesForVariant) {
        const { stage, adChannel, variant } = formData;
        if (stage && adChannel && variant) {
          maxLines = clientHandler.getMaxLinesForVariant(
            { ...formData, _creativityType: creativityType },
            stage as string,
            adChannel as string,
            variant as string
          );
        }
      }

      return <RichTextField key={field.key} {...buildProps({ ...field, maxLines })} />;
    }

    case 'visual-search':
      return <VisualSearchField key={field.key} {...buildProps({ ...field, options: getSelectOptions() })} />;

    case 'visual-search-single':
      return <VisualSearchSingleField key={field.key} {...buildProps({ ...field, options: getSelectOptions() })} />;

    case 'image-multi-select':
      return <ImageMultiSelectField key={field.key} {...buildProps({ ...field, options: getSelectOptions() })} />;

    case 'grouped-image-selector':
      return (
        <GroupedImageSelectorField
          key={field.key}
          {...buildProps(field)}
          loadSubImages={resolveLoadSubImages(field, formData)}
          groups={getGroupOptions()}
        />
      );

    case 'polymorphic':
      return (
        <PolymorphicField
          key={field.key}
          {...buildProps(field)}
          formData={formData}
          errors={errors}
          creativityType={creativityType}
        />
      );

    default:
      return null;
  }
};
